import { BehaviorSubject, combineLatest, map } from 'rxjs';
import {
  BasketRepository,
  DeepLinkData,
  ExtraRepository,
  getLanguageKeyFromLocalStorage,
  Language,
  QuoteRepository,
  RateRepository,
  RoomRepository,
  SessionDisplay,
  SessionGuests,
  SessionHelperService,
  SessionModel,
  SessionRepository,
  SessionService,
} from '@cesare';
import {
  getSessionSettingsFromSessionStorage,
  sessionStorageRemoveItem,
  removeFusionIdFromSessionStorage,
  removeSessionSettingsFromSessionStorage,
  storeSessionSettingsInSessionStorage,
  sessionStorageGetItem,
} from '../storage/storage.service';
import { Env } from '../../../../../libs/cesare/src/lib/env';
import {
  LOCATIONS,
  QwLocation,
  QwSessionSettings,
  QwBookingProgress,
} from '../../index';
import { isRunningLocally } from '../utils';

export const appLoading$ = combineLatest([
  RoomRepository.roomIsLoading$,
  SessionRepository.sessionIsLoading$,
  BasketRepository.basketIsLoading$,
  QuoteRepository.quoteIsLoading$,
  ExtraRepository.extraIsLoading$,
  RateRepository.rateIsLoading$,
]).pipe(
  map(
    ([room, session, basket, quote, extra, rate]) =>
      room || session || basket || quote || extra || rate,
  ),
);

export const sessionUpdating$ = new BehaviorSubject(false);

export const roomType$ = new BehaviorSubject<string>(
  sessionStorageGetItem('roomType') || Language.getTranslation('allRoomTypes'),
);
export const isAddingPromo$ = new BehaviorSubject<boolean>(false);

export const selectedGuests$ = new BehaviorSubject<SessionGuests>(
  getSessionSettingsFromSessionStorage()?.guests || {
    adults: window.QW_HOTEL_ENV.ADULTS,
    children: 0,
    infants: 0,
  },
);

export const selectedLocation$ = new BehaviorSubject<QwLocation>(
  getSessionSettingsFromSessionStorage()?.location,
);

export const isBookingInProgress = () => {
  const progressData = JSON.parse(sessionStorage.getItem('bookingProgress'));
  const currentDestination = getLocationValueFromUrl();
  if (progressData == null) return false;
  if (
    progressData.hotelValue == currentDestination &&
    progressData.isInProgress == true
  )
    return true;
};

export const isBookingAccommodationSatisfied = () => {
  const progressData = JSON.parse(sessionStorage.getItem('bookingProgress'));
  const currentDestination = getLocationValueFromUrl();
  if (progressData == null) return false;
  if (
    progressData.hotelValue == currentDestination &&
    progressData.isAccommodationSatisfied == true
  )
    return true;
};

export const setBookingInProgress = ({
  hotelValue,
  isInProgress = false,
  isAccommodationSatisfied = false,
}: QwBookingProgress = {}) => {
  const existingProgress = sessionStorage.getItem('bookingProgress');
  const previousProgress: QwBookingProgress = existingProgress
    ? JSON.parse(existingProgress)
    : {};
  const bookingProgress: QwBookingProgress = {
    hotelValue: hotelValue ?? previousProgress.hotelValue,
    isInProgress: isInProgress ?? previousProgress.isInProgress,
    isAccommodationSatisfied:
      isAccommodationSatisfied ?? previousProgress.isAccommodationSatisfied,
  };
  sessionStorage.setItem('bookingProgress', JSON.stringify(bookingProgress));
};

export const isInRoomsPage = () => window.location.pathname.includes('rooms');
export const isInExtraPage = () => window.location.pathname.includes('extras');
export const isInCheckoutPage = () =>
  window.location.pathname.includes('checkout');

export const isInFunnelPage = () =>
  isInRoomsPage() || isInExtraPage() || isInCheckoutPage();

export const isInGroup = () => {
  if (isRunningLocally()) return false;
  return !LOCATIONS.hostel.some((locationObject) =>
    window.location.pathname.includes(locationObject.value),
  );
};

const getHotelIDfromSessionStorage = () => {
  const location = JSON.parse(
    sessionStorage.getItem('sessionSettings') || '{}',
  )?.location;
  const hotelId = location?.hotelId || location?.['hotel_id'];
  return hotelId ? Number(hotelId) : undefined;
};

const getEngineIDfromSessionStorage = () => {
  const location = JSON.parse(
    sessionStorage.getItem('sessionSettings') || '{}',
  )?.location;
  return location?.engineId || location?.['engine_id'];
};

export const getSessionFromSessionStorageOrEnv = () => {
  const sessionStorageSessionSettings = JSON.parse(
    sessionStorage.getItem('sessionSettings') || '{}',
  );
  const stayPeriod = sessionStorageSessionSettings?.stayPeriod;
  const guests = sessionStorageSessionSettings?.guests
    ? {
        adults: Number(sessionStorageSessionSettings.guests.adults),
        children: Number(sessionStorageSessionSettings.guests.children),
        infants: 0,
      }
    : undefined;
  const display = {} as SessionDisplay;
  return {
    engineId: getEngineIDfromSessionStorage() || Env.EngineId,
    isMonoHotel: Env.IsMonoHotel,
    display: {
      culture:
        getLanguageKeyFromLocalStorage() ||
        sessionStorage.getItem('lang_js') ||
        Env.DefaultLanguage,
      currency: display.currency || Env.Currency,
      countryCode: display.countryCode || Env.Country,
    },
    context: {
      stayPeriod: stayPeriod || {
        arrivalDate: Env.DefaultArrivalDate,
        departureDate: Env.DefaultDepartureDate,
      },
      guests: guests || {
        adults: Env.DefaultNumberOfAdults,
        children: Env.DefaultNumberOfChildren,
        infants: Env.DefaultNumberOfInfants,
      },
      selectedHotelId: Number(getHotelIDfromSessionStorage() || Env.HotelId),
      promoCode: sessionStorage.getItem('promoCode'),
    },
  } as SessionModel;
};

export function setSessionSettingsToSessionStorage(
  updatedSessionSettings: QwSessionSettings,
  hotelId: any,
) {
  const sessionSettings = {
    location: LOCATIONS.hostel.find((l) => l.hotel_id === String(hotelId)),
    stayPeriod: updatedSessionSettings?.stayPeriod || {
      arrivalDate: Env.DefaultArrivalDate,
      departureDate: Env.DefaultDepartureDate,
    },

    guests: updatedSessionSettings?.guests || {
      adults: Env.DefaultNumberOfAdults,
      children: Env.DefaultNumberOfChildren,
      infants: Env.DefaultNumberOfInfants,
    },
    hasChildren:
      updatedSessionSettings?.guests.children > 0 ||
      updatedSessionSettings?.guests.infants > 0,
  };
  storeSessionSettingsInSessionStorage(sessionSettings);
}

export function clearSession() {
  removeFusionIdFromSessionStorage();
  SessionRepository.store.destroy();
}

export function deleteUserSession(
  sessionID: SessionModel['sessionId'],
  onCompleteCallback,
  leaveSessionSettings?: boolean,
) {
  return SessionService.deleteSession(sessionID).subscribe({
    complete: () => {
      removeFusionIdFromSessionStorage();
      SessionHelperService.removeSessionIdFromCookie();
      sessionStorageRemoveItem('promoCode');
      !leaveSessionSettings && removeSessionSettingsFromSessionStorage();
      if (onCompleteCallback && typeof onCompleteCallback === 'function') {
        onCompleteCallback();
      }
    },
    error: () => {
      console.log('COULD NOT DELETE SESSION');
      removeFusionIdFromSessionStorage();
      SessionHelperService.removeSessionIdFromCookie();
      sessionStorageRemoveItem('promoCode');
      window.location.reload();
    },
  });
}

export function getLocationValueFromUrl() {
  if (isInGroup()) return false;
  const currentUrl = window.location.href;
  const path = new URL(currentUrl).pathname;
  const pathParts = path.split('/').filter((part) => part); // Split and filter empty parts
  const firstPart = pathParts.length > 0 ? pathParts[0] : null;

  return firstPart;
}

export function createDeepLink(options: DeepLinkData) {
  // property=clinkilar&adults=2&children=2&currency=MYR&arrival_date=2024-12-21&departure_date=2024-12-26&promocode=test
  return `${window.location.origin}/dedge--bookingengine/?property=${options.property.toLowerCase()}&adults=${options.guests.adults.toString()}&children=${options.guests.children.toString()}${
    options.currency ? '&currency=' + options.currency : ''
  }&arrival_date=${options.stayPeriod.arrivalDate}&departure_date=${
    options.stayPeriod.departureDate
  }${options.promoCode ? '&promocode=' + options.promoCode : ''}${
    '&language=' + options.lang
  }`;
}
